import React from 'react';
import { DispositifBadge, codeDispositif } from './DispositifBadge';
import { SoldeDuPayable } from './SoldeDuPayable';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { FileText, Building2, Calendar, RefreshCw, Trash2, Eye, Receipt, Download, FilePlus, CheckCircle, Clock, Euro, FileEdit, AlertCircle, Calculator } from 'lucide-react';

export const STATUS_CONFIG = {
  'validee': { label: 'Validée', icon: CheckCircle, color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  'VALIDE': { label: 'Validée', icon: CheckCircle, color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  'brouillon': { label: 'Brouillon', icon: FileEdit, color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-50' },
  'en_attente_signature': { label: 'Attente signature', icon: Clock, color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  'en_attente_paiement': { label: 'Attente paiement', icon: Euro, color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  'en_attente': { label: 'En attente', icon: Clock, color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
  'signee': { label: 'Signée', icon: FileText, color: 'bg-indigo-500', textColor: 'text-indigo-700', bgLight: 'bg-indigo-50' },
  'paiement_valide': { label: 'Paiement validé', icon: CheckCircle, color: 'bg-green-600', textColor: 'text-green-700', bgLight: 'bg-green-50' }
};

export const TYPE_CONFIG = {
  'contrat': { label: 'Contrat', icon: FileText, color: 'from-blue-500 to-indigo-600' },
  'avenant': { label: 'Avenant', icon: FilePlus, color: 'from-purple-500 to-violet-600' }
};

export const getMontantTTC = (sub) => {
  if (sub.montant_ttc) return sub.montant_ttc;
  const tarif = sub.tarification || {};
  const montantHT = tarif.montant_ht || sub.montant_ht_contribution || sub.montant_ht || 0;
  return tarif.montant_ttc || montantHT * 1.275;
};

export const getDocType = (sub) =>
  (sub.type_document === 'avenant' || sub.numero_police?.startsWith('AV-') ? 'avenant' : 'contrat');

export const SouscriptionBrouillonCard = ({ sub, navigate, onReprendre, onDelete, onDownloadRecuESS, onDownloadRecu, onDownloadEcheancier, onRouvrirSimulateur }) => {
  const docType = getDocType(sub);
  const typeConfig = TYPE_CONFIG[docType];
  const montantTTC = getMontantTTC(sub);
  const montantHT = sub.tarification?.montant_ht || sub.montant_ht_contribution || sub.montant_ht || 0;
  const TypeIcon = typeConfig.icon;
  const isAvenant = docType === 'avenant';
  const canDownloadRecu = sub.paiement_valide || sub.statut === 'validee' || sub.statut === 'VALIDE';
  const statusConfig = STATUS_CONFIG[sub.statut] || { label: sub.statut, color: 'bg-gray-500', icon: AlertCircle };

  return (
      <Card 
        key={sub.id} 
        className={`group overflow-hidden transition-all duration-300 hover:shadow-xl border-2 ${
          isAvenant 
            ? 'border-purple-200 hover:border-purple-400' 
            : 'border-blue-200 hover:border-blue-400'
        }`}
      >
        {/* Header gradient */}
        <div className={`h-2 bg-gradient-to-r ${typeConfig.color}`} />
        
        <CardContent className="p-5">
          {/* En-tête avec type et statut */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${typeConfig.color} shadow-lg`}>
                <TypeIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <Badge variant="outline" className={`mb-1 ${isAvenant ? 'border-purple-300 text-purple-700' : 'border-blue-300 text-blue-700'}`}>
                  {typeConfig.label}
                </Badge>
                <p className="text-xs text-gray-500 font-mono truncate max-w-[200px]" title={sub.numero_police}>
                  {sub.numero_police || sub.id?.substring(0, 20) + '...'}
                </p>
              </div>
            </div>
            {(() => { const StatusIcon = statusConfig.icon; return (
            <Badge className={`${statusConfig.color} text-white gap-1 font-medium`}>
              <StatusIcon className="h-3 w-3" />
              {statusConfig.label}
            </Badge>
          ); })()}
          </div>

          {/* Raison sociale */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-gray-400" />
              <h3 className="font-bold text-gray-900 text-lg truncate">
                {sub.raison_sociale || 'Entreprise non renseignée'}
              </h3>
            </div>
            {sub.siret && (
              <p className="text-xs text-gray-500 ml-6">SIRET: {sub.siret}</p>
            )}
            <div className="ml-6 mt-1"><DispositifBadge code={codeDispositif(sub)} testId={`member-dispositif-badge-${sub.id}`} /></div>
            {sub.solde_du && <div className="mt-3"><SoldeDuPayable solde={sub.solde_du} /></div>}
          </div>

          {/* Montants */}
          <div className={`p-4 rounded-xl mb-4 ${isAvenant ? 'bg-purple-50' : 'bg-blue-50'}`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Montant de base</p>
                <p className="text-lg font-bold text-gray-700">
                  {montantHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Montant Global</p>
                <p className={`text-xl font-black ${isAvenant ? 'text-purple-600' : 'text-blue-600'}`}>
                  {montantTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
            </div>
          </div>

          {/* Date et période */}
          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Créé le {new Date(sub.date_creation || sub.created_at).toLocaleDateString('fr-FR')}</span>
            </div>
            {sub.date_fin && (
              <span className="text-xs">
                Expire le {new Date(sub.date_fin).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {/* Bouton Reprendre pour brouillons ET souscriptions en attente non payées */}
            {(sub.statut === 'brouillon' || 
              ((sub.statut === 'en_attente' || sub.statut === 'en_attente_paiement' || sub.statut === 'en_attente_signature' || sub.statut === 'signee') && !sub.paiement_valide)) ? (
              <>
                <Button 
                  size="sm"
                  onClick={() => onReprendre(sub.id)}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprendre
                </Button>
                {onRouvrirSimulateur && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRouvrirSimulateur(sub)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`btn-rouvrir-simulateur-${sub.id}`}
                          aria-label="Rouvrir dans le simulateur BATICO avec les activités cochées"
                        >
                          <Calculator className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Rouvrir dans le simulateur BATICO avec les activités cochées</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {sub.statut === 'brouillon' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(sub.id)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            ) : (
              <>
                {onRouvrirSimulateur && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRouvrirSimulateur(sub)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`btn-rouvrir-simulateur-${sub.id}`}
                          aria-label="Rouvrir dans le simulateur BATICO"
                        >
                          <Calculator className="h-4 w-4 mr-1" />
                          Simulateur
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Rouvrir dans le simulateur BATICO (activités et CA restaurés)</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {canDownloadRecu && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDownloadRecu(sub)}
                          className="flex-1"
                        >
                          <Receipt className="h-4 w-4 mr-2" />
                          Reçu
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Télécharger le reçu de paiement</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {sub.recu_ess_id && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDownloadRecuESS(sub)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          ESS
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Télécharger le reçu ESS</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {/* V36.7 — Mini-PDF Échéancier seul. Affiché si TTC > 2500 (sinon backend renvoie 400). */}
                {(((sub.montant_ttc || sub.tarification?.montant_ttc || 0) > 2500) || sub.mode_paiement === 'sepa' || sub.echeancier_cb || (sub.paiements_partiels || []).length > 0) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onDownloadEcheancier(sub)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`btn-echeancier-${sub.id}`}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Échéancier
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Télécharger l'échéancier (1 page, RIB + QR)</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/member/souscription/${sub.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Voir les détails</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </CardContent>
      </Card>
  );
};
